// Content locales for the multilingual editor (titles, descriptions, tasks).
// An organization enables an arbitrary set of locale codes via its `languages`
// setting — any code works; names are resolved with Intl.DisplayNames.

export type Locale = string;

/** German is always present and is the required / fallback locale. */
export const REQUIRED_LOCALE = 'de';

// Suggested codes for the Settings "add language" picker (ISO 639-1). Authors
// can also type any other code (free entry) — this list is only suggestions.
export const COMMON_LOCALES: string[] = [
  'de', 'en', 'fr', 'es', 'it', 'pt', 'nl', 'pl', 'ru', 'uk', 'tr', 'ar', 'he',
  'fa', 'ur', 'hi', 'bn', 'zh', 'ja', 'ko', 'vi', 'th', 'id', 'ms', 'cs', 'sk',
  'hu', 'ro', 'bg', 'el', 'sv', 'da', 'fi', 'nb', 'sr', 'hr', 'sl', 'lt', 'lv',
  'et', 'sq', 'sw',
];

const RTL_BASES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'ug', 'yi', 'dv', 'ckb']);

/** True for right-to-left scripts (input/text direction). */
export function isRtlLocale(code: string): boolean {
  return RTL_BASES.has(code.toLowerCase().split('-')[0]);
}

/**
 * Resolve a multilingual content value (title / description / task) to a
 * display string **plus the locale it resolved to** for the given UI language.
 *
 * Resolution order: the UI language's base code (so an `ar`-browser
 * participant sees Arabic content when the org authored it) → German
 * (required locale) → English. Falls through **empty strings**, not just
 * missing keys: content is frequently authored in German only, leaving the
 * other locales as `""`, and `??` would stop at that empty string and render
 * a blank.
 *
 * The resolved locale lets callers set the text direction (`dirAttr`) —
 * content RTL is per-field, the UI chrome stays LTR.
 */
export function resolveLocalized(
  ml: Record<string, string> | null | undefined,
  uiLang: string,
): { text: string; locale: Locale | null } {
  if (!ml) return { text: '', locale: null };
  const base = uiLang.toLowerCase().split('-')[0];
  for (const candidate of [base, REQUIRED_LOCALE, 'en']) {
    if (ml[candidate]) return { text: ml[candidate], locale: candidate };
  }
  return { text: '', locale: null };
}

/**
 * Resolve a multilingual content value to a single display string. Returns
 * `''` only when nothing is set; callers that want an id/placeholder fallback
 * append `|| fallback`. See `resolveLocalized` for the resolution order.
 */
export function localizedText(
  ml: Record<string, string> | null | undefined,
  uiLang: string,
): string {
  return resolveLocalized(ml, uiLang).text;
}

/**
 * `dir` attribute value for content resolved to `locale` — `'rtl'` for
 * right-to-left scripts, `undefined` (inherit LTR) otherwise.
 */
export function dirAttr(locale: Locale | null): 'rtl' | undefined {
  return locale && isRtlLocale(locale) ? 'rtl' : undefined;
}

/** Human-readable language name for any code, localized to the UI language. */
export function localeLabel(code: string, uiLang?: string): string {
  try {
    const name = new Intl.DisplayNames([uiLang || 'en'], { type: 'language' }).of(code);
    if (name && name.toLowerCase() !== code.toLowerCase()) return name;
  } catch {
    /* unknown code — fall through */
  }
  return code.toUpperCase();
}

/**
 * Normalize an organization's enabled content locales: trim/lowercase, dedupe,
 * drop blanks, and always include the required locale first (catalog order
 * otherwise preserved from the org's list).
 */
export function resolveContentLocales(orgLanguages: readonly string[] | undefined | null): Locale[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of [REQUIRED_LOCALE, ...(orgLanguages ?? [])]) {
    const code = (raw ?? '').trim().toLowerCase();
    if (code && !seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  }
  return out;
}
